import React, { useCallback, useState } from 'react'

import {
  Action,
  FieldSpec,
  LinkedField,
  LinkedPathField,
  RelatedLinkedField,
  PaginatedTable,
  ValueField,
} from 'src/components/Table'
import { Place } from 'src/api/models'
import { PlaceLabel } from 'src/components/PrintLabel'
import { BulkPrint } from 'src/components/Print/Bulk'
import { MultiPlaceFilter } from 'src/modules/shared/components/filters'
import { useAsyncPrint } from 'src/components/ToPrint/useAsyncPrint'
import { ListTableProps } from './types'

const fieldOptions: { [key: string]: FieldSpec } = {
  name: { id: 'name', label: 'Name', renderer: LinkedField(ValueField) },
  facility: {
    id: 'facility',
    label: 'Facility',
    ordering: 'facility__name',
    renderer: RelatedLinkedField(ValueField, 'name'),
  },
  path: {
    id: 'objPath',
    label: 'Storage Path',
    ordering: 'search_path',
    renderer: LinkedPathField,
  },
}

export const PlaceListTable = (props: ListTableProps) => {
  const {
    paginator,
    fields,
    bulkActions=[],
    exportConfig,
    extraActions,
    extraBulkActions=[],
    extraRowActions,
    title = 'Places',
    onSelectedItem,
  } = props
  const [selected, setSelected] = useState<Place[]>([])
  const { print, renderPrintComponent } = useAsyncPrint()

  const bulkOptions: { [key: string]: Action } = {
    printPlace: {
      label: 'Print Place',
      action: () => {
        print((props) => (
          <BulkPrint
            {...props}
            items={selected}
            render={({ obj, onReadyToPrint }) => {
              onReadyToPrint?.()
              return <PlaceLabel obj={obj} />
            }}
          />
        ))
      },
    },
  }

  const onSelected = useCallback((selected: Place[]) => {
    onSelectedItem?.(selected)
    setSelected(selected)
  }, [onSelectedItem])

  return (
    <>
      {renderPrintComponent()}
      <PaginatedTable
        title={title}
        fields={fields.map((field) => fieldOptions[field])}
        paginator={paginator}
        onSelectedItem={(bulkActions.length || onSelectedItem) ? onSelected : undefined}
        bulkActions={bulkActions.map((action) => bulkOptions[action]).concat(extraBulkActions)}
        filters={[
          {
            id: 'ancestors',
            label: 'Descendants Of',
            widget: MultiPlaceFilter,
          },
          {
            id: 'parents',
            label: 'Children Of',
            widget: MultiPlaceFilter,
          },
          {
            id: 'children',
            label: 'Has Children',
            options: { '1': 'True', '0': 'False' },
          },
          {
            id: 'empty',
            label: 'Is Empty',
            options: { '1': 'True', '0': 'False' },
          },
        ]}
        actions={extraActions}
        exportConfig={exportConfig}
        rowActions={extraRowActions}
      />
    </>
  )
}
